import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AlignJustify,
  LogOut,
  LayoutDashboard,
  ChevronLeft,
  ChevronRight,
  ShieldUser,
} from 'lucide-react';
import { usePermisos } from '../context/PermisosContext';
import { getApiUrl } from '../utils/apiConfig';

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  
  // 🔐 Obtener permisos de acceso
  const { tieneAccesoVisitas, tieneAccesoObras, isAdmin, loadingPermisos } = usePermisos();

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // En móvil, siempre cerrado por defecto
      if (mobile) {
        setSidebarOpen(false);
      }
    };

    // Ejecutar al montar el componente
    handleResize();

    // Escuchar cambios de tamaño
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Efecto separado para manejar el scroll del body (ya no necesario en móvil)
  useEffect(() => {
    if (sidebarOpen && isMobile) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [sidebarOpen, isMobile]);


  const handleLogout = async () => {
    try {
      const response = await fetch(getApiUrl('/api/logout'), {
        method: 'POST',
        credentials: 'include',
      });
      if (response.ok) {
        window.location.href = '/';
      } else {
        console.error('Error al cerrar sesión');
      }
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      window.location.href = '/';
    }
  };

  const navigateToDashboard = () => {
    navigate('/dashboard');
    // Cerrar sidebar en móvil después de navegar
    if (isMobile) {
      setSidebarOpen(false);
    }
  };
  
  const navigateToVisitas = () => {
    if (!tieneAccesoVisitas && !loadingPermisos) {
      // No hacer nada si no tiene acceso
      return;
    }
    navigate('/dashboard/visitas');
    // Cerrar sidebar en móvil después de navegar
    if (isMobile) {
      setSidebarOpen(false);
    }
  };
  
  const navigateToObras = () => {
    if (!tieneAccesoObras && !loadingPermisos) {
      // No hacer nada si no tiene acceso
      return;
    }
    navigate('/dashboard/obras');
    // Cerrar sidebar en móvil después de navegar
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const navigateToAdministracion = () => {
    if (!isAdmin && !loadingPermisos) {
      // No hacer nada si no es admin
      return;
    }
    navigate('/dashboard/administracion');
    // Cerrar sidebar en móvil después de navegar
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const isDashboardActive = location.pathname === '/dashboard';
  const isVisitasActive = location.pathname === '/dashboard/visitas';
  const isObrasActive = location.pathname === '/dashboard/obras';
  const isAdministracionActive = location.pathname === '/dashboard/administracion';

  return (
    <div className="flex h-screen bg-gray-100 no-select">
      {/* Botón flotante SOLO en móvil cuando el sidebar está cerrado */}
      {isMobile && !sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          className="fixed top-4 right-4 z-50 p-3 bg-white rounded-xl shadow-lg border border-gray-200 hover:bg-gray-50 transition-all duration-300"
          aria-label="Abrir menú"
        >
          <AlignJustify size={24} className="text-gray-700" />
        </button>
      )}

      {/* Sidebar */}
      <aside
        className={`${
          isMobile 
            ? sidebarOpen ? 'w-16' : 'w-0'  // Móvil: solo w-16 (colapsado) o w-0 (oculto)
            : sidebarOpen ? 'w-60' : 'w-16' // Desktop: w-60 (expandido) o w-16 (colapsado)
        } bg-white shadow-xl border-r border-gray-200 flex-shrink-0 transition-all duration-300 ease-out relative sticky top-0 h-screen overflow-hidden z-50`}
      >
        <nav className="h-full flex flex-col justify-between relative p-4">
          <div className="space-y-3">
            {/* Header con logo */}
            <div className="flex items-center justify-center align-center mb-6">
              {/* Logo - Solo visible en desktop cuando está expandido */}
              {!isMobile && (
                <div className={`flex items-center gap-3 transition-all duration-300 ease-out overflow-hidden ${
                  sidebarOpen ? 'opacity-100 max-w-xs' : 'opacity-0 max-w-0'
                }`}>
                  <div className="whitespace-nowrap">
                    <h1 className="text-xl font-bold text-gray-800">BME CRM</h1>
                    <p className="text-xs text-gray-500">Gestión comercial</p>
                  </div>
                </div>
              )}
              
              {/* Botón toggle - Comportamiento diferente en móvil y desktop */}
              <button
                className={`p-2 rounded-xl hover:bg-gray-200 transition-all duration-300 ease-out ${
                  isMobile ? 'mx-auto' : sidebarOpen ? 'ml-auto' : 'mx-auto'
                }`}
                onClick={() => {
                  setSidebarOpen(!sidebarOpen);
                }}
              >
                <div className="relative w-5 h-5">
                  {isMobile ? (
                    // En móvil: Solo X para cerrar
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  ) : (
                    // En desktop: Toggle normal (flecha/hamburguesa)
                    <>
                      <ChevronLeft 
                        size={20} 
                        className={`absolute inset-0 text-gray-600 transition-all duration-300 ease-out ${
                          sidebarOpen ? 'opacity-100 rotate-0' : 'opacity-0 rotate-90'
                        }`} 
                      />
                      <AlignJustify 
                        size={20} 
                        className={`absolute inset-0 text-gray-600 transition-all duration-300 ease-out ${
                          sidebarOpen ? 'opacity-0 -rotate-90' : 'opacity-100 rotate-0'
                        }`} 
                      />
                    </>
                  )}
                </div>
              </button>
            </div>

            <hr className="border-gray-200" />

            {/* Navigation items */}
            <div className="space-y-2">
              <button
                className={`w-full flex items-center px-4 py-2 rounded-xl transition-all duration-300 group relative
                  ${isDashboardActive
                    ? 'bg-blue-100 bg-gradient-to-r from-blue-50 to-blue-100 to-blue-200 border border-blue-400 shadow-sm text-blue-600'
                    : 'border hover:bg-blue-50 hover:shadow-sm hover:border-blue-400 hover:text-blue-600 shadow-sm'
                  }
                  ${(sidebarOpen && !isMobile) ? 'gap-3' : 'justify-center'}`}
                onClick={navigateToDashboard}
              >
                <LayoutDashboard size={18} className="flex-shrink-0" />
                <span className={`font-medium text-sm whitespace-nowrap transition-all duration-300 ease-out overflow-hidden ${
                  (sidebarOpen && !isMobile) ? 'opacity-100 max-w-xs ml-3' : 'opacity-0 max-w-0 ml-0'
                }`}>
                  Dashboard
                </span>
                {/* <div className={`absolute left-full ml-2 px-2 py-1 bg-gray-800 text-white text-xs rounded-md whitespace-nowrap z-50 transition-all duration-200 pointer-events-none ${
                  !sidebarOpen ? 'opacity-0 group-hover:opacity-100' : 'opacity-0'
                }`}>
                  Dashboard
                </div> */}
              </button>

              <hr className="border-gray-200" />

              {/* Botón VISITAS */}
              <button
                disabled={!tieneAccesoVisitas && !loadingPermisos}
                className={`w-full flex items-center px-4 py-2 rounded-xl transition-all duration-300 group relative
                  ${!tieneAccesoVisitas && !loadingPermisos
                    ? 'opacity-40 cursor-not-allowed bg-gray-50 border border-gray-200'
                    : isVisitasActive
                    ? 'bg-blue-100 bg-gradient-to-r from-blue-50 to-blue-100 to-blue-200 border border-blue-400 shadow-sm text-blue-600'
                    : 'border hover:bg-blue-50 hover:shadow-sm hover:border-blue-400 hover:text-blue-600 shadow-sm'
                  }
                  ${(sidebarOpen && !isMobile) ? 'gap-3' : 'justify-center'}`}
                onClick={navigateToVisitas}
                title={!tieneAccesoVisitas && !loadingPermisos ? 'No tienes acceso a esta sección' : 'Ir a Visitas'}
              >
                {/* Ícono: Candado si no tiene acceso, Visitas si tiene acceso */}
                {!tieneAccesoVisitas && !loadingPermisos ? (
                  <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                )}
                <span className={`font-medium text-sm whitespace-nowrap transition-all duration-300 ease-out overflow-hidden ${
                  (sidebarOpen && !isMobile) ? 'opacity-100 max-w-xs ml-3' : 'opacity-0 max-w-0 ml-0'
                }`}>
                  Visitas
                </span>
              </button>

              <hr className="border-gray-200" />

              {/* Botón OBRAS */}
              <button
                disabled={!tieneAccesoObras && !loadingPermisos}
                className={`w-full flex items-center px-4 py-2 rounded-xl transition-all duration-300 group relative
                  ${!tieneAccesoObras && !loadingPermisos
                    ? 'opacity-40 cursor-not-allowed bg-gray-50 border border-gray-200'
                    : isObrasActive
                    ? 'bg-blue-100 bg-gradient-to-r from-blue-50 to-blue-100 to-blue-200 border border-blue-400 shadow-sm text-blue-600'
                    : 'border hover:bg-blue-50 hover:shadow-sm hover:border-blue-400 hover:text-blue-600 shadow-sm'
                  }
                  ${(sidebarOpen && !isMobile) ? 'gap-3' : 'justify-center'}`}
                onClick={navigateToObras}
                title={!tieneAccesoObras && !loadingPermisos ? 'No tienes acceso a esta sección' : 'Ir a Obras'}
              >
                {/* Ícono: Candado si no tiene acceso, Obras si tiene acceso */}
                {!tieneAccesoObras && !loadingPermisos ? (
                  <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                )}
                <span className={`font-medium text-sm whitespace-nowrap transition-all duration-300 ease-out overflow-hidden ${
                  (sidebarOpen && !isMobile) ? 'opacity-100 max-w-xs ml-3' : 'opacity-0 max-w-0 ml-0'
                }`}>
                  Obras
                </span>
              </button>

              <hr className="border-gray-200" />

              {/* Botón ADMINISTRACIÓN - Solo para ADMIN */}
              {/* <button
                disabled={!isAdmin && !loadingPermisos}
                className={`w-full flex items-center px-4 py-2 rounded-xl transition-all duration-300 group relative
                  ${!isAdmin && !loadingPermisos
                    ? 'opacity-40 cursor-not-allowed bg-gray-50 border border-gray-200'
                    : isAdministracionActive
                    ? 'bg-blue-100 bg-gradient-to-r from-blue-50 to-blue-100 to-blue-200 border border-blue-400 shadow-sm text-blue-600'
                    : 'border hover:bg-blue-50 hover:shadow-sm hover:border-blue-400 hover:text-blue-600 shadow-sm'
                  }
                  ${(sidebarOpen && !isMobile) ? 'gap-3' : 'justify-center'}`}
                onClick={navigateToAdministracion}
                title={!isAdmin && !loadingPermisos ? 'No tienes acceso a esta sección' : 'Ir a Administración'}
              >
                {!isAdmin && !loadingPermisos ? (
                  <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <ShieldUser size={18} className="flex-shrink-0" />
                )}
                <span className={`font-medium text-sm whitespace-nowrap transition-all duration-300 ease-out overflow-hidden ${
                  (sidebarOpen && !isMobile) ? 'opacity-100 max-w-xs ml-3' : 'opacity-0 max-w-0 ml-0'
                }`}>
                  Administración
                </span>
              </button>

              <hr className="border-gray-200" /> */}



            </div>


          </div>

          {/* Footer con logout */}
          <div className="space-y-3">
            <hr className="border-gray-200" />
            
            <button
              className={`w-full flex items-center px-4 py-2 rounded-xl transition-all duration-300 group border relative
                ${(sidebarOpen && !isMobile) ? 'gap-3' : 'justify-center'} hover:bg-red-100 hover:text-red-500 hover:border-red-200 hover:shadow-sm`}
              onClick={handleLogout}
            >
              <LogOut size={18} className="flex-shrink-0" />
              <span className={`font-medium text-sm whitespace-nowrap transition-all duration-300 ease-out overflow-hidden ${
                (sidebarOpen && !isMobile) ? 'opacity-100 max-w-xs ml-3' : 'opacity-0 max-w-0 ml-0'
              }`}>
                Cerrar sesión
              </span>
              {/* <div className={`absolute left-full ml-2 px-2 py-1 bg-gray-800 text-white text-xs rounded-md whitespace-nowrap z-50 transition-all duration-200 pointer-events-none ${
                !sidebarOpen ? 'opacity-0 group-hover:opacity-100' : 'opacity-0'
              }`}>
                Cerrar sesión
              </div> */}
            </button>
          </div>
        </nav>
      </aside>

      {/* Overlay para móviles */}
      {sidebarOpen && isMobile && (
        <div
          className="fixed inset-0 bg-black/30 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Contenido Principal */}
      <main className="flex-1 h-screen overflow-y-auto bg-white transition-all duration-300 ease-out">
        <Outlet />
      </main>
    </div>
  );
};

export default Dashboard;
